import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ProjectManager.css';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewingNotes, setViewingNotes] = useState(null);
  const [projectNotes, setProjectNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    budget: '',
    deadline: '',
    priority: 'medium',
    status: 'pending',
    requirements: '',
    assignedTo: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchFreelancers();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/projects/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchFreelancers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users/freelancers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreelancers(response.data);
    } catch (error) {
      console.error('Error fetching freelancers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = { ...formData };
      
      // Convert empty assignedTo to null
      if (!dataToSend.assignedTo) {
        delete dataToSend.assignedTo;
      }

      if (editingProject) {
        await axios.put(`/api/projects/${editingProject.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/projects', dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchProjects();
      resetForm();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      category: project.category,
      budget: project.budget,
      deadline: project.deadline?.split('T')[0] || '',
      priority: project.priority,
      status: project.status,
      requirements: project.requirements || '',
      assignedTo: project.assignedTo || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το έργο;')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const assignFreelancer = async (projectId, freelancerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/projects/${projectId}/assign`, 
        { freelancerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProjects();
    } catch (error) {
      console.error('Error assigning freelancer:', error);
      alert('Failed to assign freelancer');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      budget: '',
      deadline: '',
      priority: 'medium',
      status: 'pending',
      requirements: '',
      assignedTo: ''
    });
    setEditingProject(null);
    setShowForm(false);
  };

  const getFreelancerName = (freelancerId) => {
    const freelancer = freelancers.find(f => f.id === freelancerId);
    return freelancer ? freelancer.name : 'Μη ανατεθέν';
  };

  const fetchProjectNotes = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/projects/${projectId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setProjectNotes([]);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) {
      alert('Παρακαλώ εισάγετε σημείωση');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/projects/${viewingNotes.id}/notes`, 
        { message: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewNote('');
      fetchProjectNotes(viewingNotes.id);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Αποτυχία προσθήκης σημείωσης');
    }
  };

  const openNotesModal = (project) => {
    setViewingNotes(project);
    fetchProjectNotes(project.id);
  };

  return (
    <div className="project-manager">
      <div className="manager-header">
        <h2>Διαχείριση Έργων</h2>
        <button className="btn-add" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Ακύρωση' : '+ Νέο Έργο'}
        </button>
      </div>

      {showForm && (
        <form className="project-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Τίτλος *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Κατηγορία *</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Προϋπολογισμός (€) *</label>
              <input
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({...formData, budget: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Προθεσμία *</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Προτεραιότητα</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
              >
                <option value="low">Χαμηλή</option>
                <option value="medium">Μεσαία</option>
                <option value="high">Υψηλή</option>
              </select>
            </div>

            <div className="form-group">
              <label>Κατάσταση</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="pending">Αναμονή</option>
                <option value="in-progress">Σε Εξέλιξη</option>
                <option value="completed">Ολοκληρωμένο</option>
                <option value="cancelled">Ακυρωμένο</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Περιγραφή *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Απαιτήσεις</label>
              <textarea
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                rows="3"
              />
            </div>

            <div className="form-group full-width">
              <label>Ανάθεση σε Freelancer</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
              >
                <option value="">Δεν έχει ανατεθεί</option>
                {editingProject?.interestedFreelancers?.length > 0 && (
                  <optgroup label="Έχουν δηλώσει ενδιαφέρον">
                    {freelancers
                      .filter(f => editingProject.interestedFreelancers.includes(f.id))
                      .map(freelancer => (
                        <option key={freelancer.id} value={freelancer.id}>
                          ⭐ {freelancer.name} ({freelancer.email})
                        </option>
                      ))
                    }
                  </optgroup>
                )}
                <optgroup label={editingProject?.interestedFreelancers?.length > 0 ? "Άλλοι Freelancers" : "Όλοι οι Freelancers"}>
                  {freelancers
                    .filter(f => !editingProject?.interestedFreelancers?.includes(f.id))
                    .map(freelancer => (
                      <option key={freelancer.id} value={freelancer.id}>
                        {freelancer.name} ({freelancer.email})
                      </option>
                    ))
                  }
                </optgroup>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-save">
              {editingProject ? 'Ενημέρωση' : 'Δημιουργία'}
            </button>
            <button type="button" className="btn-cancel" onClick={resetForm}>
              Ακύρωση
            </button>
          </div>
        </form>
      )}

      <div className="projects-table">
        <table>
          <thead>
            <tr>
              <th>Τίτλος</th>
              <th>Κατηγορία</th>
              <th>Προϋπολογισμός</th>
              <th>Προθεσμία</th>
              <th>Κατάσταση</th>
              <th>Ενδιαφέρον</th>
              <th>Ανατέθηκε σε</th>
              <th>Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(project => (
              <tr key={project.id}>
                <td>{project.title}</td>
                <td>{project.category}</td>
                <td>€{project.budget}</td>
                <td>{new Date(project.deadline).toLocaleDateString('el-GR')}</td>
                <td>
                  <span className={`status-badge status-${project.status}`}>
                    {project.status}
                  </span>
                </td>
                <td>
                  {project.interestedFreelancers && project.interestedFreelancers.length > 0 ? (
                    <div className="interest-tooltip-wrapper">
                      <span className="interest-badge">
                        {project.interestedFreelancers.length} 👥
                      </span>
                      <div className="interest-tooltip">
                        <div className="tooltip-title">Ενδιαφέρον έχουν δηλώσει:</div>
                        {project.interestedFreelancers.map(id => (
                          <div key={id} className="tooltip-freelancer">
                            • {getFreelancerName(id)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <span className="no-interest">-</span>
                  )}
                </td>
                <td>{getFreelancerName(project.assignedTo)}</td>
                <td className="actions">
                  <button className="btn-notes" onClick={() => openNotesModal(project)}>
                    Σημειώσεις
                  </button>
                  <button className="btn-edit" onClick={() => handleEdit(project)}>
                    Επεξεργασία
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(project.id)}>
                    Διαγραφή
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {projects.length === 0 && (
          <p className="no-data">Δεν υπάρχουν έργα ακόμα</p>
        )}
      </div>

      {viewingNotes && (
        <div className="modal-overlay" onClick={() => setViewingNotes(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Σημειώσεις - {viewingNotes.title}</h2>
              <button className="modal-close" onClick={() => setViewingNotes(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h3>Σημειώσεις & Σχόλια</h3>
                <div className="notes-container">
                  {projectNotes.length === 0 ? (
                    <p className="no-notes">Δεν υπάρχουν σημειώσεις για αυτό το έργο.</p>
                  ) : (
                    <div className="notes-list">
                      {projectNotes.map(note => (
                        <div key={note.id} className="note-item">
                          <div className="note-header">
                            <span className="note-author">
                              {note.userName} 
                              <span className="note-role">({note.userRole === 'freelancer' ? 'Freelancer' : note.userRole === 'admin' ? 'Admin' : 'Πελάτης'})</span>
                            </span>
                            <span className="note-date">
                              {new Date(note.createdAt).toLocaleString('el-GR')}
                            </span>
                          </div>
                          <div className="note-message">{note.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="add-note-form">
                    <textarea
                      className="note-input"
                      placeholder="Προσθέστε μια σημείωση..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows="3"
                    />
                    <button className="btn-add-note" onClick={addNote}>
                      Προσθήκη Σημείωσης
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;
